import React from 'react';
import styled, { css } from 'styled-components';

type Appearance = 'default' | 'primary' | 'warning';
type Spacing = 'compact' | 'default' | 'none';

export type CompatButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Define o estilo visual principal do botão. */
    appearance?: Appearance;
    /** Densidade do botão. */
    spacing?: Spacing;
    /** Quando true, o botão ocupa 100% da largura do container. */
    shouldFitContainer?: boolean;
  };

const resolveAppearance = (appearance: Appearance) => {
  switch (appearance) {
    case 'primary':
      return css`
        background: #004ab7;
        color: #ffffff;
        &:hover {
          filter: brightness(0.96);
        }
        &:active {
          transform: translateY(1px);
        }
        &:disabled {
          background: #9bb9ee;
          cursor: not-allowed;
        }
      `;
    case 'warning':
      return css`
        background: #cf9d08;
        color: #101010;
        &:hover {
          filter: brightness(0.96);
        }
        &:active {
          transform: translateY(1px);
        }
        &:disabled {
          background: #e8d49a;
          cursor: not-allowed;
        }
      `;
    default:
      return css`
        background: #dcdfe0;
        color: #1f2d3d;
        &:hover {
          filter: brightness(0.98);
        }
        &:active {
          transform: translateY(1px);
        }
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `;
  }
};

const resolveSpacing = (spacing: Spacing) => {
  switch (spacing) {
    case 'compact':
      return css`
        padding: 0.375rem 0.5rem;
        min-height: 28px;
        font-size: 0.875rem;
      `;
    case 'none':
      return css`
        padding: 0;
        min-height: auto;
      `;
    default:
      return css`
        padding: 0.5rem 0.75rem;
        min-height: 36px;
        font-size: 0.9rem;
      `;
  }
};

const BaseButton = styled.button<
  Required<
    Pick<CompatButtonProps, 'appearance' | 'spacing' | 'shouldFitContainer'>
  >
>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  border: 0;
  border-radius: 6px;
  font-weight: 600;
  line-height: 1;
  user-select: none;
  transition: filter 0.15s ease, transform 0.02s ease;
  white-space: nowrap;

  ${({ shouldFitContainer }) =>
    shouldFitContainer &&
    css`
      width: 100%;
    `}
  ${({ spacing }) => resolveSpacing(spacing)}
  ${({ appearance }) => resolveAppearance(appearance)}

  &:focus-visible {
    outline: 2px solid #0b5cff;
    outline-offset: 2px;
  }
`;

/**
 * CompatButton
 * - Props suportadas: `appearance` ('default' | 'primary' | 'warning'),
 *   `spacing` ('compact' | 'default' | 'none'), `shouldFitContainer`,
 *   além de todas as props nativas de <button>.
 * - Acessível (focus ring) e pronto para uso em layouts responsivos.
 */
export const CompatButton: React.FC<CompatButtonProps> = ({
  appearance = 'default',
  spacing = 'default',
  shouldFitContainer = false,
  children,
  ...rest
}) => (
  <BaseButton
    appearance={appearance}
    spacing={spacing}
    shouldFitContainer={shouldFitContainer}
    {...rest}
  >
    {children}
  </BaseButton>
);

export default CompatButton;
